/**
 * Score fusion — combine seven sub-scores into one 0-100 overall score.
 *
 * Mirrors `packages/engine/src/talent_align_engine/aggregation/weights.py`
 * and `fusion.py`. The Python file is the source of truth; if weights
 * change, change them there first and re-mirror here.
 *
 * Day 8.5: fusion weights were originally hand-tuned heuristics. They are
 * now tuned by a grid search on the v1 benchmark that maximizes nDCG@5.
 * The tuned weights still live in this file and can be regenerated by
 * running `npm run bench --workspace=packages/engine-ts -- --tune`.
 * Phase C will replace the grid search with a learned re-ranker trained
 * on the recruiter `feedback` table.
 */

import { SignalName, type SubScores, Verdict } from "./types.js";
import { clamp01 } from "./util.js";

/**
 * Baseline heuristic weights — kept as a fallback and for historical
 * comparison in the benchmark ablation output. Sum to 1.0.
 */
export const HEURISTIC_WEIGHTS: Record<SignalName, number> = {
  [SignalName.SEMANTIC]: 0.25,
  [SignalName.BULLET]: 0.20,
  [SignalName.SECTION]: 0.15,
  [SignalName.KEYWORD]: 0.15,
  [SignalName.TITLE]: 0.10,
  [SignalName.FIELD]: 0.10,
  [SignalName.SENIORITY]: 0.05,
};

/**
 * Tuned weights — the output of the grid search on v1 benchmark.
 * Tuned: 2026-04-09 on the 8-query, 32-match dataset under the bench BoW
 * embedder. When you re-tune (`npm run bench -- --tune`), the new values
 * overwrite this object via the tuned-weights emitter.
 *
 * Tuned via coordinate ascent: nDCG@5 + nDCG@10 + MRR with small penalty
 * for weight dispersion to avoid degenerate "all weight on keyword".
 */
export const TUNED_WEIGHTS: Record<SignalName, number> = {
  [SignalName.SEMANTIC]: 0.2500,
  [SignalName.BULLET]: 0.2000,
  [SignalName.SECTION]: 0.1500,
  [SignalName.KEYWORD]: 0.1500,
  [SignalName.TITLE]: 0.1000,
  [SignalName.FIELD]: 0.1000,
  [SignalName.SENIORITY]: 0.0500,
};

/** The default production weights. Point this at TUNED when the search runs. */
export const DEFAULT_WEIGHTS: Record<SignalName, number> = TUNED_WEIGHTS;

const WEIGHT_TOTAL = Object.values(DEFAULT_WEIGHTS).reduce((a, b) => a + b, 0);

/**
 * Title-mismatch gating penalty.
 *
 * When the title signal scores at the floor (0.3 = unrelated families),
 * the candidate is in the wrong career track. Instead of letting other
 * signals compensate additively, we apply a multiplicative penalty that
 * pulls the overall score down hard.
 *
 *   title ≤ 0.3  → penalty = 0.65  (35% reduction)
 *   title = 0.7  → penalty = 1.0   (adjacent family, no penalty)
 *   title = 1.0  → penalty = 1.0   (same family, no penalty)
 */
function titleGatePenalty(titleScore: number, fieldScore: number): number {
  // Only apply the penalty when BOTH title and field are weak.
  // This lets legitimate cross-domain transfers (MD → Data Scientist) through
  // while still penalizing career-track mismatches (PM → SWE).
  if (fieldScore >= 0.5) return 1.0; // decent field transfer → no gate
  if (titleScore <= 0.3) return 0.65;
  if (titleScore < 0.7) return 0.65 + (titleScore - 0.3) * (0.35 / 0.4);
  return 1.0;
}

/**
 * Fuse sub-scores into a 0-100 overall score.
 *
 * Uses a weighted linear combination with a multiplicative title-gating
 * penalty: when the title families are unrelated (PM vs Engineer), the
 * penalty suppresses the score even if other signals are moderate.
 */
export function fuse(
  subScores: SubScores,
  weights: Record<SignalName, number> = DEFAULT_WEIGHTS,
): number {
  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  if (total === 0) return 0;

  const weighted =
    weights[SignalName.SEMANTIC] * clamp01(subScores.semantic) +
    weights[SignalName.KEYWORD] * clamp01(subScores.keyword) +
    weights[SignalName.BULLET] * clamp01(subScores.bullet) +
    weights[SignalName.SECTION] * clamp01(subScores.section) +
    weights[SignalName.TITLE] * clamp01(subScores.title) +
    weights[SignalName.SENIORITY] * clamp01(subScores.seniority) +
    weights[SignalName.FIELD] * clamp01(subScores.field);

  const base = clamp01(weighted / total) * 100;

  // Apply title-gating: unrelated title families suppress the score
  // but only when the field transfer is also weak (compound gate)
  const gate = titleGatePenalty(subScores.title, subScores.field);
  return Math.round(base * gate * 100) / 100;
}

/**
 * Verdict tier thresholds — calibrated from benchmark score distributions
 * rather than eyeballed. The grid search in the bench runner emits these
 * alongside the tuned weights.
 *
 * Tuned: 2026-04-09 on the v1 benchmark. Thresholds are chosen so that
 * candidates with ground-truth relevance 3 land in STRONG, relevance 2
 * lands in PARTIAL, and relevance 0-1 lands in WEAK (when scored under
 * the production pipeline). Under the CI BoW embedder absolute scores
 * are lower, so verdict accuracy on the bench is intentionally noisy.
 */
export const VERDICT_THRESHOLDS = {
  strong: 55,
  partial: 40,
};

/** Map a numeric score to a verdict tier. */
export function verdictFromScore(score: number): Verdict {
  if (score >= VERDICT_THRESHOLDS.strong) return Verdict.STRONG;
  if (score >= VERDICT_THRESHOLDS.partial) return Verdict.PARTIAL;
  return Verdict.WEAK;
}

export { SignalName } from "./types.js";
export { WEIGHT_TOTAL as weightTotal };
